import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import classNames from "classnames"
import Box from "@material-ui/core/Box"
import styles from "./CompanyProfileContainer.module.scss"
import PhotoGalleryWrap from "../../components/PhotoGallery/PhotoGalleryWrap"
import VideosPreviewer from "../../components/Videos/VideosPreviewer"
import AddProfilePhotoPopup from "../../components/AddProfilePhotoPopup/AddProfilePhotoPopup"
import useWindowHeightWidth from "../../hooks/useWindowHeightWidth"
import {
  userDeletesCompanyAvatar,
  userChangeCompanyLogo,
  userChangeFullName,
} from "../../actions/user/userActions"
import services from "../../services"
import CompanyUserInfo from "../../components/CompanyUserInfo/CompanyUserInfo"
import CompanyDepartments from "../../components/CompanyDepartments/CompanyDepartments"
import CompanyOpportunities from "../../components/CompanyOpportunities/CompanyOpportunities"
import CompanyPrivateOpportunities from "../../components/CompanyPrivateOpportunities/CompanyPrivateOpportunities"
import CompanyResources from "../../components/CompanyResources"
import ProfileMyQuestions from "../../components/ProfileMyQuestions"
import {COMPANY_EMPLOYEE, COMPANY_SEEKER, COMPANY_PROVIDER} from "../../helpers/userRoles"
import CompanyEmployUserInfo from "../../components/CompanyUserInfo/CompanyEmployUserInfo"
import XYCenterAlignedWindow from "../../components/XYCenterAlignedWindow"
import ResetPasswordError from "../../components/ResetPasswordError/ResetPasswordError"

const CompanyProfileContainer = () => {
  const [showPhotoPopup, setShowPhotoPopup] = useState(false)
  const [data, setData] = useState({})
  const { width } = useWindowHeightWidth()
  const userInfo = useSelector((state) => state.user)
  const isEmploy = useSelector((state) => state.user).role === COMPANY_EMPLOYEE

  // Map level_type enum to Elite tag display name
  const getEliteTagFromLevelType = (levelType, companyType) => {
    // Only show tags for company_seeker, not for company_provider
    if (companyType !== 'company_seeker') {
      return null
    }
    
    if (!levelType || levelType === 'not_set') {
      return null
    }
    
    // Map level_type enum to display name
    switch (levelType) {
      case 'elite':
        return 'Elite'
      case 'elite_plus':
        return 'Elite Plus'
      case 'elite_premium':
        return 'Elite Premium'
      default:
        return null
    }
  }

  const dispatcher = useDispatch()
  useEffect(() => {
    services.companyServices.getSelfCompanyInfo().then((result) => {
      let res = {
        ...result.data,
        opportunities: result.data.opportunities.sort((a, b) => b.id - a.id),
        private_opportunities: result.data.private_opportunities
          ? result.data.private_opportunities.sort((a, b) => b.id - a.id)
          : [],
        // Calculate elite_tag from level_type enum
        elite_tag: getEliteTagFromLevelType(result.data.level_type, result.data.company_type),
      }
      setData(res)
      dispatcher(userChangeCompanyLogo(result.data.photo || ""))
    })
  }, [dispatcher])

  const updateCompanyUser = (params) =>
    services.companyServices
      .updateCompanyUser(params)
      .then((result) => setData({ ...data, ...result.data }))

  const updateEmployUser = (params) =>
    services.userServices.updateUsers(params).then((result) => {
      dispatcher(userChangeFullName(result.data.full_name))
    })

  const onSaveProfilePhoto = async (
    {
      file,
      croppedImage,
      cropData: { x, y, height, width },
    },
    onProgress
  ) => {
    const photoData = {
      photo: croppedImage,
      origin_photo: file,
      x,
      y,
      height,
      width,
    }

    try {
      const result = await services.companyServices.setCompanyLogo(
        photoData,
        onProgress
      )
      setData({ ...data, ...result.data })
      dispatcher(userChangeCompanyLogo(result.data.photo))
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  const onDeletePhotoProfile = () => {
    dispatcher(userDeletesCompanyAvatar())
    setData({
      ...data,
      x: null,
      y: null,
      width: null,
      height: null,
      photo: null,
      origin_photo: null,
    })
  }

  const headBtnWidth = width <= 360 ? "62px" : "182px"

  const saveNewDeparment = async ({ departmentName, employees }) => {
    try {
      const formattedData = {
        name: departmentName,
        employees_count: employees,
      }
      const result = await services.departmentsServices.createDepartment(
        userInfo.company.uuid,
        formattedData,
      )
      setData({ ...data, departments: [result.data, ...data.departments] })
    } catch (e) {
      throw e
    }
  }

  const onAddPhotos = (photos) => {
    const newPhotos = [...data.photo_gallery, ...photos]
    setData({ ...data, photo_gallery: newPhotos })
  }

  const onDeletePhoto = (uuid) => {
    let newPhotos = [...data.photo_gallery]
    newPhotos = newPhotos.filter((photo) => photo.uuid !== uuid)
    setData({ ...data, photo_gallery: newPhotos })
  }

  const onDeleteVideo = (uuid) => {
    let newVideos = [...data.video_gallery]
    services.userServices.deleteVideo(uuid).then(() => {
      newVideos = newVideos.filter((video) => video.uuid !== uuid)
      setData({ ...data, video_gallery: newVideos })
    })
  }

  const onAddVideo = (videos) => {
    const newVideos = [...data.video_gallery, ...videos]
    setData({ ...data, video_gallery: newVideos })
  }

  if (!data?.company_type) {
    return null
  }

  if (data?.blocked) {
    return (
      <XYCenterAlignedWindow
        children={
          <ResetPasswordError
            errorMessage={"Please contact support for the solution."}
            title={
              "Your profile has been turned down because of inappropriate activity."
            }
          />
        }
      />
    )
  }

  if (data)
    return (
      <>
        <div
          className={classNames("container", "fullWidth", styles.profileWrap)}
        >
          {showPhotoPopup && (
            <AddProfilePhotoPopup
              onClose={() => {
                setShowPhotoPopup(false)
              }}
              onSave={onSaveProfilePhoto}
              url={data.origin_photo}
              fallbackUrl={data.photo}
              currentData={{
                x: data.x,
                y: data.y,
                width: data.width,
                height: data.height,
              }}
            />
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ width: "100%" }}
          >
            <div className={styles.profileLeftColumn}>
              {isEmploy ? (
                <CompanyEmployUserInfo
                  activity={data.activity}
                  describeMe={data.describe_company}
                  descriptionProp={data.description}
                  employName={userInfo.fullName}
                  name={userInfo.company.name}
                  companyType={data.company_type}
                  image={userInfo.company.photo}
                  openPhotoPopup={() => {
                    setShowPhotoPopup(true)
                  }}
                  onDeletePhotoProfile={onDeletePhotoProfile}
                  updateCompanyUser={updateEmployUser}
                  industr={data.industry}
                  isEmployee={isEmploy}
                />
              ) : (
                <CompanyUserInfo
                  activity={data.activity}
                  describeMe={data.describe_company}
                  descriptionProp={data.description}
                  markets={data.markets}
                  classifications={data.classifications}
                  certifications={data.certifications}
                  industr={data.industry}
                  diverse={data.diverse}
                  name={data.name}
                  companyType={data.company_type}
                  image={userInfo.company.photo}
                  openPhotoPopup={() => {
                    setShowPhotoPopup(true)
                  }}
                  onDeletePhotoProfile={onDeletePhotoProfile}
                  updateCompanyUser={updateCompanyUser}
                  eliteTag={data.elite_tag}
                  verificationTag={data.verification_tag}
                />
              )}
              <PhotoGalleryWrap
                className={classNames(styles.galleryWrap, styles.tablet)}
                type="preview"
                data={data.photo_gallery}
                onAddPhotos={onAddPhotos}
                onDeletePhoto={onDeletePhoto}
                verificationTag={data.verification_tag}
                eliteTag={data.elite_tag}
              />
              <VideosPreviewer
                className={styles.videosWrap}
                videos={data.video_gallery}
                onDeleteVideo={onDeleteVideo}
                onAddVideo={onAddVideo}
                anotherUser={false}
                userType="companies"
              />
            </div>
            <div className={styles.profileRightColumn}>
              <PhotoGalleryWrap
                className={styles.galleryWrap}
                type="preview"
                data={data.photo_gallery}
                onAddPhotos={onAddPhotos}
                onDeletePhoto={onDeletePhoto}
                verificationTag={data.verification_tag}
                eliteTag={data.elite_tag}
              />

              <div className={styles.departmentsContainer}>
                <CompanyDepartments
                  data={data.departments}
                  saveNewDeparment={saveNewDeparment}
                  headBtnWidth={headBtnWidth}
                  width={width}
                  uuid={userInfo.company.uuid}
                />
                  {userInfo?.company?.type !== COMPANY_SEEKER && (
                    <>
                      <CompanyOpportunities
                        data={data}
                        headBtnWidth={headBtnWidth}
                        width={width}
                        uuid={userInfo.company.uuid}
                      />
                      {userInfo?.company?.type === COMPANY_PROVIDER && (
                        <CompanyPrivateOpportunities
                          data={data}
                          headBtnWidth={headBtnWidth}
                          width={width}
                          uuid={userInfo.company.uuid}
                        />
                      )}
                    </>
                  )}
                <CompanyResources
                  data={data.company_resources}
                  userInfo={userInfo}
                  width={width}
                />

                <ProfileMyQuestions
                  data={data.community_topics}
                  width={width}
                />
              </div>
            </div>
          </Box>
        </div>
      </>
    )
}

export default CompanyProfileContainer
